/**
 * VoicePill — Floating top pill for voice mode.
 * State machine: idle | listening | thinking | speaking (KURAL 100).
 * KURAL 99: truncated → 'Ekrana yazildi, oku?' prompt.
 */
import { AnimatePresence, motion } from 'framer-motion'
import { Mic, Volume2, X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useVoiceWebSocket } from '../../hooks/useVoiceWebSocket'
import './VoicePill.css'

export type VoicePillState = 'idle' | 'listening' | 'thinking' | 'speaking'

interface VoicePillProps {
  active: boolean
  onClose: () => void
}

const dimensions: Record<VoicePillState, { width: number; height: number }> = {
  idle: { width: 0, height: 0 },
  listening: { width: 220, height: 40 },
  thinking: { width: 260, height: 44 },
  speaking: { width: 300, height: 44 },
}

export function VoicePill({ active, onClose }: VoicePillProps) {
  const { voiceState, aiText, truncated, connect, disconnect, sendInterrupt, language } = useVoiceWebSocket()

  const state = (voiceState === 'error' ? 'idle' : voiceState) as VoicePillState

  const handleClose = () => {
    disconnect()
    onClose()
  }

  // Auto-connect when active
  if (active && state === 'idle' && voiceState !== 'error') {
    connect()
  }

  if (!active || state === 'idle') return null

  const { width, height } = dimensions[state]

  const pill = (
    <AnimatePresence>
      <motion.div
        className="voice-pill"
        initial={{ width: 100, height: 28, opacity: 0 }}
        animate={{ width, height, opacity: 1 }}
        exit={{ width: 100, height: 28, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        layout
      >
        <motion.div
          className="voice-pill__content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {state === 'listening' && <ListeningContent language={language} />}
          {state === 'thinking' && <ThinkingContent />}
          {state === 'speaking' && (
            <SpeakingContent message={aiText} truncated={truncated} onInterrupt={sendInterrupt} />
          )}
          <button className="voice-pill__close" onClick={handleClose} aria-label="Close voice mode">
            <X size={10} />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )

  return createPortal(pill, document.body)
}

function ListeningContent({ language }: { language?: string | null }) {
  return (
    <>
      {language && <span className="voice-lang-chip">{language.toUpperCase()}</span>}
      <Mic size={14} />
      <span>Dinliyorum...</span>
      <div className="voice-waveform" aria-hidden>
        {[0,1,2,3,4].map(i => (
          <span key={i} className="voice-waveform-bar" style={{ animationDelay: `${i*0.08}s` }} />
        ))}
      </div>
    </>
  )
}

function ThinkingContent() {
  return (
    <>
      <div className="voice-pill__dots">
        <span /><span /><span />
      </div>
      <span>Dusunuyor...</span>
    </>
  )
}

function SpeakingContent({ message, truncated, onInterrupt }: { message?: string; truncated?: boolean; onInterrupt: () => void }) {
  return (
    <>
      <Volume2 size={14} onClick={onInterrupt} style={{ cursor: 'pointer' }} />
      {truncated
        ? <span>Ekrana yazildi, oku?</span>
        : <span>{message && message.length > 30 ? message.slice(0, 30) + '...' : message}</span>
      }
    </>
  )
}
